import React from 'react';
import {useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {useTheme} from 'styled-components';

import {EChannelType, TItemId} from 'src/constants';
import {
  EType,
  EVariableType,
  TMessageMatcher,
  TVariables,
} from 'src/containers/MessageMatchers/definitions';
import {
  getPlaceholdersFromRegexTemplate,
  getPlaceholdersFromVariableTemplate,
  getWordsFromRegexTemplate,
  getWordsFromVariableTemplate,
} from 'src/containers/MessageMatchers/utils';
import {useGetAllParametersQuery} from 'src/containers/Parameters/api';

import {
  getTextRegex,
  makeEmptyVariableSpan,
  makePlaceholderSpan,
  makeVariableSpan,
  prepareMessageText,
} from './utils';

type TProps = {
  channelType: TMessageMatcher['channelType'];
  text?: string;
  type?: TMessageMatcher['type'];
  parentText?: string;
  tenantId?: TItemId;
  variablesPath?: string;
};

type TTextProps = {
  placeholders: string[];
  words: string[];
  hasRegexes?: boolean;
  tenantId?: TItemId;
  variablesPath?: string;
};

const UserText = React.memo<TTextProps>(
  ({hasRegexes, placeholders, words, tenantId, variablesPath = 'variables'}) => {
    const {t} = useTranslation();
    const theme = useTheme();

    const {data: parameters = []} = useGetAllParametersQuery({tenantId});

    const methods = useFormContext();

    let variables = methods?.watch(variablesPath) as TVariables[];

    if (!variables) {
      variables = placeholders.map((placeholder) => ({
        type: EVariableType.PARAMS,
        value: `{{${placeholder}}}`,
      }));
    }

    variables = variables.filter(
      (variable: TVariables) => hasRegexes || variable?.type !== EVariableType.TEXT,
    );

    return (
      <span
        dangerouslySetInnerHTML={{
          __html: words
            .flatMap((word, index) => {
              const result = word.split(' ');

              if (placeholders[index] !== undefined) {
                const variable = variables[index];
                const value = variable?.value ?? '';

                if (variable?.type === EVariableType.PARAMS && !value && hasRegexes) {
                  result.push(makeEmptyVariableSpan({theme, text: t('MessagePreview:variable')}));

                  return result;
                }

                const option = parameters.find(
                  (item) => String(item.id) === String(value).replace(/[{}]/g, ''),
                );

                if (option || (variable?.type === EVariableType.TEXT && value)) {
                  result.push(makeVariableSpan({theme, text: option?.name ?? value}));

                  return result;
                }

                if (hasRegexes) {
                  result.push(
                    makePlaceholderSpan({
                      theme,
                      text: placeholders[index],
                    }),
                  );

                  return result;
                }

                result.push(
                  makePlaceholderSpan({
                    theme,
                    text: t('MessagePreview:placeholder', {index: index + 1}),
                  }),
                );
              }

              return result;
            })
            .join(' ')
            .trim(),
        }}
      />
    );
  },
);

const CustomText = React.memo<TTextProps>(({placeholders, words, tenantId}) => {
  const {t} = useTranslation();
  const theme = useTheme();

  const {data: parameters = []} = useGetAllParametersQuery({tenantId});

  return (
    <span
      dangerouslySetInnerHTML={{
        __html: words
          .flatMap((word, index) => {
            const result = word.split(' ');

            if (placeholders[index] !== undefined) {
              const option = parameters.find(
                (item) => String(item.id) === String(placeholders[index]),
              );

              if (option) {
                result.push(makeVariableSpan({theme, text: option.name}));

                return result;
              }

              result.push(makeEmptyVariableSpan({theme, text: t('MessagePreview:variable')}));
            }

            return result;
          })
          .join(' ')
          .trim(),
      }}
    />
  );
});

const OperatorText = React.memo<TTextProps>(({hasRegexes, placeholders, words}) => {
  const {t} = useTranslation();
  const theme = useTheme();

  return (
    <span
      dangerouslySetInnerHTML={{
        __html: words
          .flatMap((word, index) => {
            const result = word.split(' ');

            if (placeholders[index] !== undefined) {
              if (hasRegexes) {
                result.push(
                  makePlaceholderSpan({
                    theme,
                    text: placeholders[index],
                  }),
                );
              } else {
                result.push(
                  makePlaceholderSpan({
                    theme,
                    text: t('MessagePreview:placeholder', {index: index + 1}),
                  }),
                );
              }
            }

            return result;
          })
          .join(' ')
          .trim(),
      }}
    />
  );
});

export const PreviewText = React.memo<TProps>(
  ({channelType, type = EType.CUSTOM, text, parentText, variablesPath, tenantId}) => {
    if (!text) {
      return null;
    }

    const matcherType =
      channelType === EChannelType.VIBER && type === EType.USER ? EType.CUSTOM : type;
    const regexesRegex = getTextRegex(channelType, matcherType);
    const matcherText = matcherType === EType.USER && regexesRegex ? parentText ?? text : text;
    const formattedText = prepareMessageText({channelType, text: matcherText});
    const words = regexesRegex
      ? getWordsFromRegexTemplate(formattedText, regexesRegex)
      : getWordsFromVariableTemplate(formattedText);

    if (!words?.length) {
      return null;
    }

    const placeholders = regexesRegex
      ? getPlaceholdersFromRegexTemplate(formattedText, regexesRegex)
      : getPlaceholdersFromVariableTemplate(formattedText);

    if (matcherType === EType.OPERATOR) {
      return <OperatorText placeholders={placeholders} words={words} hasRegexes={!!regexesRegex} />;
    }

    if (matcherType === EType.USER) {
      return (
        <UserText
          placeholders={placeholders}
          words={words}
          hasRegexes={!!regexesRegex}
          tenantId={tenantId}
          variablesPath={variablesPath}
        />
      );
    }

    return <CustomText placeholders={placeholders} words={words} tenantId={tenantId} />;
  },
);

PreviewText.displayName = 'Text';
